import { ArrowRight, ImageIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { EditableSectionProps } from '@/types/theme';

export function EditableSectionTemplate({ eyebrow, title, description, primaryCta, secondaryCta, image }: EditableSectionProps) {
	return (
		<section className="relative overflow-hidden rounded-[2rem] border border-border bg-card px-8 py-12 shadow-glow md:px-12 md:py-16">
			<div className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top,rgba(124,63,0,0.2),transparent_65%)]" aria-hidden="true" />
			<div className="relative grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
				<div className="max-w-3xl">
					{eyebrow ? (
						<div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-accent">
							<ImageIcon className="h-4 w-4" />
							{eyebrow}
						</div>
					) : null}
					{title ? <h2 className="mt-6 font-serif text-5xl leading-none tracking-tight text-foreground md:text-7xl">{title}</h2> : null}
					{description ? <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">{description}</p> : null}
					<div className="mt-8 flex flex-wrap gap-4">
						{primaryCta?.label ? (
							<a href={primaryCta.url || '/'}>
								<Button size="lg">
									{primaryCta.label}
									<ArrowRight className="ml-2 h-4 w-4" />
								</Button>
							</a>
						) : null}
						{secondaryCta?.label ? (
							<a href={secondaryCta.url || '/'}>
								<Button variant="outline" size="lg">
									{secondaryCta.label}
								</Button>
							</a>
						) : null}
					</div>
				</div>

				{image?.url ? (
					<div className="overflow-hidden rounded-[1.75rem] border border-border/80 bg-background/80 p-3 backdrop-blur">
						<img src={image.url} alt={image.alt || title || 'Section image'} className="h-full min-h-72 w-full rounded-[1.2rem] object-cover" />
					</div>
				) : (
					<div className="grid min-h-72 place-items-center rounded-[1.75rem] border border-dashed border-border/80 bg-background/80 p-6 text-center text-muted-foreground backdrop-blur">
						<p>Add an editable image field in the block schema if this component needs media.</p>
					</div>
				)}
			</div>
		</section>
	);
}
